import { Figura, tetoDoEixo } from './base'

/**
 * Ranking em barras horizontais — produtividade por profissional.
 *
 * **Não é SVG de propósito.** Nome de gente é longo ("Dra. Ana
 * Beatriz Cardoso"), e SVG não quebra nem corta texto: ou vaza por
 * cima do gráfico ou vira `textLength` esmagado. Em HTML o nome
 * trunca com reticências, o leitor de tela lê a lista na ordem certa
 * e o layout responde sozinho no celular. É a referência 7 — barra de
 * progresso dentro da linha.
 *
 * Por que barra horizontal e não coluna: coluna com nome embaixo
 * força rótulo girado a 45°, que é ilegível e é a assinatura visual
 * de painel malfeito.
 *
 * Por que UMA cor para todas as barras: as pessoas não têm ordem
 * intrínseca, então isto é categórico nominal — e categórico nominal
 * com N cores gasta o canal de identidade para repetir o que o
 * comprimento da barra já diz. A identidade de cada profissional
 * entra num ponto de 10px com a cor DELE (a mesma da agenda), do lado
 * do nome, onde ela serve para reconhecer e não para comparar.
 *
 * Por que não pizza: comparar seis fatias parecidas é a coisa que o
 * olho faz pior. Ângulo é a pior codificação de magnitude que existe.
 *
 * Duas medidas (atendimentos e faturamento) NUNCA dividem o mesmo
 * gráfico com dois eixos. São dois gráficos, ou um seletor — a prop
 * `acao` existe para o seletor.
 */

export type LinhaRanking = {
  id: string
  nome: string
  valor: number
  /** A cor do profissional (`profissional.cor`). Só o ponto, nunca a barra. */
  cor?: string | null
  /** Meta da competência, quando existe. Vira uma régua sobre a barra. */
  meta?: number | null
  /** Segunda informação da linha: "82% de aproveitamento", "12 faltas". */
  nota?: string
}

export function BarrasRanking({
  titulo, subtitulo, linhas, formatar, unidade, acao, vazio,
}: {
  titulo: string
  subtitulo?: string
  /** Já ordenadas por quem chama — a ordem é dado, não enfeite. */
  linhas: LinhaRanking[]
  formatar: (v: number) => string
  /** "atendimentos", "marcações" — entra na leitura do leitor de tela. */
  unidade: string
  acao?: React.ReactNode
  vazio?: React.ReactNode
}) {
  const maiorValor = Math.max(0, ...linhas.map((l) => l.valor))
  const maiorMeta = Math.max(0, ...linhas.map((l) => l.meta ?? 0))
  // O eixo cobre a meta também: barra que "estoura" a régua da meta
  // porque a régua ficou fora da escala é pior que não ter régua.
  const teto = tetoDoEixo(Math.max(maiorValor, maiorMeta)) || 1

  if (linhas.length === 0 && vazio) return <>{vazio}</>

  return (
    <Figura
      titulo={titulo} subtitulo={subtitulo} acao={acao}
      tabela={{
        colunas: ['Quem', unidade.charAt(0).toUpperCase() + unidade.slice(1), 'Meta', 'Observação'],
        linhas: linhas.map((l) => [
          l.nome, formatar(l.valor),
          l.meta != null ? formatar(l.meta) : '—',
          l.nota ?? '—',
        ]),
      }}>
      <ol className="gfx-rank">
        {linhas.map((l) => {
          const pctBarra = (l.valor / teto) * 100
          const pctMeta = l.meta != null ? (l.meta / teto) * 100 : null
          const bateuMeta = l.meta != null && l.valor >= l.meta
          return (
            <li key={l.id} className="gfx-rank__linha">
              <span className="gfx-rank__quem">
                {/* A cor escolhida pela clínica não passa por
                    validação de contraste — por isso ela só aparece
                    como marca de identidade de 10px ao lado do nome,
                    e nunca como preenchimento que alguém precise
                    comparar ou ler texto por cima. */}
                <span className="gfx-rank__ponto" aria-hidden="true"
                      style={l.cor ? { background: l.cor } : undefined} />
                <span className="gfx-rank__nome" title={l.nome}>{l.nome}</span>
              </span>

              <span className="gfx-rank__trilho">
                <span className="gfx-rank__barra" style={{ width: `${pctBarra}%` }} />
                {pctMeta != null && pctMeta <= 100 && (
                  <span className="gfx-rank__meta" style={{ left: `${pctMeta}%` }}
                        title={`Meta: ${formatar(l.meta!)}`} />
                )}
              </span>

              <span className="gfx-rank__valor">
                {formatar(l.valor)}
                {l.meta != null && (
                  // Bateu a meta não é só cor: é palavra.
                  <span className={bateuMeta ? 'gfx-rank__selo gfx-rank__selo--ok'
                                             : 'gfx-rank__selo'}>
                    {bateuMeta ? '✓ meta' : `meta ${formatar(l.meta)}`}
                  </span>
                )}
              </span>

              {l.nota && <span className="gfx-rank__nota">{l.nota}</span>}
            </li>
          )
        })}
      </ol>
    </Figura>
  )
}
